import { gql, useLazyQuery } from '@apollo/client'
import { BoxSubscription } from '@jusbrasil-web/shared-apollo-client/__generated__/types'
import { usePromise } from '@jusbrasil-web/shared-use-promise'
import { useCallback, useEffect, useRef } from 'react'

import {
  RefreshSubscriptionQuery,
  RefreshSubscriptionQueryVariables,
  UseWaitSubscriptionUpdate_OrganizationFragment,
  UseWaitSubscriptionUpdate_ViewerFragment,
} from './__generated__/use-wait-subscription-update.graphql'

type UseWaitSubscriptionUpdateProps = {
  viewer: UseWaitSubscriptionUpdate_ViewerFragment
}

type PromiseResult = {
  result: boolean
  currentSubscription: Pick<BoxSubscription, 'subscriptionId' | 'type' | 'orderCode'> | null
}

type WaitSubscriptionUpdate = (orderCode: string) => Promise<PromiseResult>

type UseWaitSubscriptionUpdateReturn = {
  waitSubscriptionUpdate: WaitSubscriptionUpdate
}

const ORGANIZATION_FRAGMENT = gql`
  fragment useWaitSubscriptionUpdate_organization on Organization {
    owner {
      subscriptions {
        subscriptionId
        type
        orderCode
      }
    }
  }
`

export const REFRESH_SUBSCRIPTION_QUERY = gql`
  query RefreshSubscription($orderCode: String!) {
    root {
      viewer: me {
        currentOrganization {
          ...useWaitSubscriptionUpdate_organization
        }
        patinhasSubscription(orderCode: $orderCode) {
          status
        }
      }
    }
  }

  ${ORGANIZATION_FRAGMENT}
`

const INTERVAL = process.env.NODE_ENV === 'test' ? 10 : 1000

function getPaidSubscription(
  currentOrganization: UseWaitSubscriptionUpdate_OrganizationFragment | null
) {
  const orgOwnerSubscriptions = currentOrganization?.owner?.subscriptions || []
  return (
    orgOwnerSubscriptions.find((s) => s?.type === 'SUBSCRIPTION' && s?.orderCode !== null) || null
  )
}

export function useWaitSubscriptionUpdate(
  props: UseWaitSubscriptionUpdateProps
): UseWaitSubscriptionUpdateReturn {
  // TODO: replace with subscription when available. @felipemsantana
  // https://www.apollographql.com/docs/react/data/subscriptions
  const [fetchQuery, { called, data, startPolling, stopPolling }] = useLazyQuery<
    RefreshSubscriptionQuery,
    RefreshSubscriptionQueryVariables
  >(REFRESH_SUBSCRIPTION_QUERY)

  const { promise, resolve } = usePromise<PromiseResult>()
  const oldSubscription = useRef(getPaidSubscription(props.viewer.currentOrganization)).current

  useEffect(() => {
    if (!called || !data?.root.viewer) return

    const { currentOrganization, patinhasSubscription } = data.root.viewer
    const currentSubscription = getPaidSubscription(currentOrganization)

    if (
      (patinhasSubscription?.status === 'allowed' &&
        oldSubscription?.subscriptionId !== currentSubscription?.subscriptionId) ||
      patinhasSubscription?.status === 'denied'
    ) {
      stopPolling()
      resolve({ result: patinhasSubscription.status === 'allowed', currentSubscription })
    } else {
      startPolling(INTERVAL)
    }
  }, [
    called,
    data?.root.viewer,
    oldSubscription?.subscriptionId,
    props.viewer.currentOrganization,
    resolve,
    startPolling,
    stopPolling,
  ])

  const waitSubscriptionUpdate: WaitSubscriptionUpdate = useCallback(
    (orderCode) => {
      fetchQuery({ variables: { orderCode } })
      return promise
    },
    [fetchQuery, promise]
  )

  return { waitSubscriptionUpdate }
}

useWaitSubscriptionUpdate.fragments = {
  viewer: gql`
    fragment useWaitSubscriptionUpdate_viewer on Profile {
      currentOrganization {
        ...useWaitSubscriptionUpdate_organization
      }
    }

    ${ORGANIZATION_FRAGMENT}
  `,
}
